// Built-in libraries
import { CalendarIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/solid';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { deleteDraw } from '@/api/Sherpa/Draw';
import ReactSelect from '@/components/UI/Form/ReactSelect';
import Confirm from '@/components/UI/Modal/Confirm';
import { SherpaProjectReactQueryKey } from '@/constants/ReactQueryKeys';
// Custom Hooks
import { useDraw, useDrawDropdown } from '@/hooks/ReactQueryHooks/useProjectDrawQuery';
import { useProject, useProjectTeamMembers } from '@/hooks/ReactQueryHooks/useProjectQuery';
import { notifyUser } from '@/store/Toast';
// Assets & Miscellaneous
import { formatDate3, sanitizeResponseError } from '@/utils';
// Components
import { EditDraw } from '../../EditDraw';
import DrawDetailsApprovers from './DrawDetailsApprovers';

const DrawDetailsHeader = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectId, drawId } = useParams();
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [selectedDrawId, setSelectedDrawId] = useState<number>(Number(drawId));

  const getProjectTeamMembers = useProjectTeamMembers({
    projectId: Number(projectId),
  });
  const projectTeamMembers = getProjectTeamMembers?.data?.data?.internal || [];

  const {
    data: drawData,
    refetch: refetchDraw,
    isLoading,
  } = useDraw({
    projectId: Number(projectId),
    drawId: Number(selectedDrawId),
  });

  const draw = drawData?.data;

  useEffect(() => {
    if (selectedDrawId) {
      refetchDraw();
    }
  }, [selectedDrawId, refetchDraw]);

  const fetchDrawDropdown = useDrawDropdown({
    projectId: Number(projectId),
  });
  const drawDropDown = fetchDrawDropdown?.data?.data;

  const fetchProject = useProject({ projectId: Number(projectId) });
  const project = fetchProject?.data?.data;

  const [isEditing, setIsEditing] = useState<boolean>(false);

  const invoiceApprover = projectTeamMembers.find((member) => member.id === draw?.invoiceApproverId);
  const documentApprover = projectTeamMembers.find((member) => member.id === draw?.documentApproverId);

  const handleModel = () => {
    setIsEditing(!isEditing);
  };

  const removeDrawHandler = () => {
    setShowRemoveConfirm(true);
  };

  const cancelRemoveDraw = () => {
    setShowRemoveConfirm(false);
  };

  const removeDraw = useMutation(
    () =>
      deleteDraw({
        projectId: Number(projectId),
        drawId: Number(drawId),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_PROJECT_DRAWS]);
        navigate(`/app/sherpa/projects/${projectId}/idms`);

        setShowRemoveConfirm(false);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Project draw deleted successfully',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const deleteDrawHandler = () => {
    removeDraw.mutate();
  };

  const handleChangeDraw = (option: any) => {
    setSelectedDrawId(option.value.toString());
    navigate(`/app/sherpa/projects/${projectId}/idms/${option.value}`);
  };

  return (
    <>
      {isEditing && <EditDraw handleModal={handleModel} draw={draw} projectName={project?.name || ''} />}

      {showRemoveConfirm && (
        <Confirm
          title='Remove Draw'
          message={`You are about to remove draw ${draw?.sequenceNum}. Are you sure you want to remove ?`}
          acceptButtonLabel='Remove'
          declineButtonLabel='Cancel'
          accept={() => {
            deleteDrawHandler();
          }}
          decline={cancelRemoveDraw}
        />
      )}
      {draw && (
        <div className='mb-1 w-full inset-x-0 mx-auto -bottom-20 z-10 flex flex-col justify-between gap-4 bg-white rounded-md shadow-lg items-left'>
          <div className='flex flex-col gap-3 w-1/4 mt-5 py-3 px-6'>
            <p>Select Project Draw</p>
            <ReactSelect id='leadOrgTitleId' name='leadTitle' options={drawDropDown} onChange={(option) => handleChangeDraw(option)} />
          </div>
          <div className='flex flex-row justify-between items-center border-t-2 w-full px-6'>
            <div className='flex flex-col'>
              <h2>Draw {draw?.sequenceNum}</h2>
              <div className='flex justify-items-center gap-4'>
                <div className='flex gap-2'>
                  <CalendarIcon className='text-neutralColor-normal w-5 h-5' />
                  <p>Draw Period Open Date : {formatDate3(draw?.openDate)}</p>
                </div>
                <div className='flex gap-2'>
                  <CalendarIcon className='text-neutralColor-normal w-5 h-5' />
                  <p>Draw Period Close Date : {formatDate3(draw?.closeDate)}</p>
                </div>
              </div>
            </div>
            <div className='flex gap-5 mr-12'>
              <PencilIcon className='text-neutralColor-normal w-6 h-6 hover:text-primaryColor-light cursor-pointer' onClick={handleModel} />
              <TrashIcon
                className='text-neutralColor-normal w-6 h-6 hover:text-tertiaryColor-normal cursor-pointer'
                onClick={() => removeDrawHandler()}
              />
            </div>
          </div>
          <DrawDetailsApprovers invoiceApprover={invoiceApprover!} documentApprover={documentApprover!} />
        </div>
      )}
      {isLoading && <div className='w-full h-48 my-4 rounded animate-pulse bg-neutralColor-lighter'></div>}
    </>
  );
};

export default DrawDetailsHeader;
